import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { UserContext } from '../contexts/UserContext';
import { db } from '../firebase';
import {
    collection,
    addDoc,
    onSnapshot,
    query,
    orderBy,
    serverTimestamp,
    where,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

import {
    Box,
    Button,
    Paper,
    Tabs,
    Tab,
    Typography,
    TextField,
    IconButton,
    Stack,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendIcon from '@mui/icons-material/Send';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';

const GOLD = '#d4af37';
const GOLD_SOFT = 'rgba(212,175,55,0.75)';
const GOLD_DIM = 'rgba(212,175,55,0.6)';
const TEXT_WARM = '#f9f6e3';
const BLACK = '#000';
const BLACK_DEEP = '#0a0a0a';
const PANEL_BG = 'linear-gradient(180deg, rgba(212,175,55,0.06) 0%, rgba(0,0,0,0) 40%), #0b0b0b';

const Chat = () => {
    const { userData, loginTime } = useContext(UserContext);
    const [isOpen, setIsOpen] = useState(false);
    const [tab, setTab] = useState('global');
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const navigate = useNavigate();
    const userGuildId = userData?.guildId || null;

    const endRef = useRef(null);

    // Auto-scroll to bottom on new messages
    useEffect(() => {
        if (isOpen && endRef.current) {
            endRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isOpen]);

    useEffect(() => {
        if (!isOpen || !loginTime || (tab === 'guild' && !userGuildId)) return;

        const baseQuery = query(
            collection(
                db,
                tab === 'global' ? 'globalMessages' : `guilds/${userGuildId}/messages`
            ),
            where('timestamp', '>', loginTime),
            orderBy('timestamp')
        );

        const unsub = onSnapshot(baseQuery, (snap) => {
            const msgs = snap.docs.map((doc) => doc.data());
            setMessages(msgs);
        });

        return () => unsub();
    }, [tab, isOpen, userGuildId, loginTime]);

    const sendMessage = async () => {
        if (!input.trim() || !userData) return;

        const msg = {
            text: input,
            senderName: userData.username,
            uid: userData.uid,
            timestamp: serverTimestamp(),
        };

        const ref =
            tab === 'global'
                ? collection(db, 'globalMessages')
                : collection(db, `guilds/${userGuildId}/messages`);

        await addDoc(ref, msg);
        setInput('');
    };

    const handleTabChange = (_, newValue) => setTab(newValue);

    // button label swaps for fun
    const buttonLabel = useMemo(() => (isOpen ? 'Close Chat' : 'Open Chat'), [isOpen]);

    return (
        <Box sx={{ position: 'fixed', bottom: 20, left: 20, zIndex: 1300 }}>
            {/* Toggle Button — larger + gold */}
            <Button
                variant="contained"
                startIcon={isOpen ? <ForumRoundedIcon /> : <ChatBubbleOutlineIcon />}
                onClick={() => setIsOpen((v) => !v)}
                size="large"
                sx={{
                    px: 2.25,
                    py: 1.25,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: 'none',
                    borderRadius: 2,
                    color: BLACK,
                    background: `linear-gradient(180deg, ${GOLD} 0%, #c49b2a 100%)`,
                    boxShadow: `0 4px 18px rgba(212,175,55,0.35)`,
                    '&:hover': {
                        background: `linear-gradient(180deg, #e2c35b 0%, ${GOLD} 100%)`,
                        boxShadow: `0 6px 22px rgba(212,175,55,0.5)`,
                    },
                }}
            >
                {buttonLabel}
            </Button>

            {/* Chat Panel — larger, dark + gold */}
            {isOpen && (
                <Paper
                    elevation={0}
                    sx={{
                        mt: 1.5,
                        width: 420,         // bigger
                        height: 540,        // bigger
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        borderRadius: 2,
                        background: PANEL_BG,
                        border: `1px solid rgba(212,175,55,0.28)`,
                        boxShadow: `0 12px 40px rgba(0,0,0,0.5), inset 0 0 30px rgba(212,175,55,0.06)`,
                    }}
                >
                    {/* Tabs */}
                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{
                            minHeight: 44,
                            '& .MuiTabs-flexContainer': {
                                borderBottom: `1px solid rgba(212,175,55,0.25)`,
                            },
                            '& .MuiTab-root': {
                                color: GOLD_DIM,
                                textTransform: 'none',
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                minHeight: 44,
                                '&.Mui-selected': {
                                    color: TEXT_WARM,
                                    textShadow: `0 0 10px ${GOLD_SOFT}`,
                                },
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: GOLD,
                                height: 3,
                                boxShadow: `0 0 10px ${GOLD_SOFT}`,
                            },
                        }}
                    >
                        <Tab label="Global" value="global" />
                        <Tab label="Guild" value="guild" />
                    </Tabs>

                    {/* Message Area */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            p: 1.5,
                            background: `radial-gradient(1200px 500px at 50% 120%, rgba(212,175,55,0.06), transparent 60%), radial-gradient(800px 400px at -10% -20%, rgba(212,175,55,0.04), transparent 60%)`,
                        }}
                    >
                        {tab === 'guild' && !userGuildId ? (
                            <Box textAlign="center" mt={2}>
                                <Typography sx={{ color: TEXT_WARM }}>
                                    You are not in a guild.
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        mt: 1,
                                        borderColor: GOLD_DIM,
                                        color: GOLD,
                                        textTransform: 'none',
                                        '&:hover': { borderColor: GOLD, boxShadow: `0 0 12px ${GOLD_SOFT}` },
                                    }}
                                    onClick={() => navigate('/guild')}
                                >
                                    Join or Create
                                </Button>
                            </Box>
                        ) : (
                            <>
                                {messages.map((msg, idx) => {
                                    const mine = msg.uid === userData?.uid;
                                    return (
                                        <Stack
                                            key={idx}
                                            direction="row"
                                            justifyContent={mine ? 'flex-end' : 'flex-start'}
                                            sx={{ mb: 1 }}
                                        >
                                            <Box
                                                sx={{
                                                    maxWidth: '85%',
                                                    px: 1.25,
                                                    py: 0.75,
                                                    borderRadius: mine ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                                                    background: mine
                                                        ? 'rgba(212,175,55,0.12)'
                                                        : 'rgba(255,255,255,0.04)',
                                                    border: `1px solid ${mine ? 'rgba(212,175,55,0.35)' : 'rgba(212,175,55,0.18)'}`,
                                                    boxShadow: mine
                                                        ? `0 0 12px rgba(212,175,55,0.18)`
                                                        : `0 0 8px rgba(212,175,55,0.10)`,
                                                    backdropFilter: 'blur(2px)',
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: mine ? GOLD : GOLD_DIM, fontWeight: 700 }}
                                                >
                                                    {msg.senderName}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: TEXT_WARM, lineHeight: 1.35 }}
                                                >
                                                    {msg.text}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    );
                                })}
                                <div ref={endRef} />
                            </>
                        )}
                    </Box>

                    {/* Input Area */}
                    {(tab !== 'guild' || userGuildId) && (
                        <Box
                            sx={{
                                borderTop: `1px solid rgba(212,175,55,0.25)`,
                                display: 'flex',
                                alignItems: 'center',
                                p: 1,
                                gap: 1,
                                background: `linear-gradient(0deg, rgba(0,0,0,0.6), rgba(0,0,0,0.3))`,
                            }}
                        >
                            <TextField
                                variant="outlined"
                                size="small"
                                placeholder="Compose a message…"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                                fullWidth
                                InputProps={{
                                    sx: {
                                        color: TEXT_WARM,
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(212,175,55,0.25)' },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: GOLD_DIM },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: GOLD,
                                            boxShadow: `0 0 0 2px rgba(212,175,55,0.15)`,
                                        },
                                        background: 'rgba(255,255,255,0.03)',
                                        borderRadius: 1.5,
                                    },
                                }}
                                inputProps={{ style: { paddingTop: 10, paddingBottom: 10 } }}
                            />
                            <IconButton
                                onClick={sendMessage}
                                disabled={!input.trim()}
                                sx={{
                                    bgcolor: input.trim() ? GOLD : 'rgba(212,175,55,0.35)',
                                    color: BLACK,
                                    '&:hover': {
                                        bgcolor: input.trim() ? '#e2c35b' : 'rgba(212,175,55,0.45)',
                                        boxShadow: input.trim() ? `0 0 16px ${GOLD_SOFT}` : 'none',
                                    },
                                }}
                            >
                                <SendIcon />
                            </IconButton>
                        </Box>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default Chat;
